import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import TVCard from '@/components/base/TVCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import CategoryTabs from '@/components/base/CategoryTabs';
import { tvSeriesData, categories } from '@/mocks/tvSeries';

const categoryNames: Record<string, string> = {
  all: '全部剧集',
  chinese: '国产剧',
  korean: '韩剧',
  american: '美剧',
  japanese: '日剧',
  anime: '动漫',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const activeSlug = slug || 'all';
  const [sortBy, setSortBy] = useState<'latest' | 'rating' | 'popular'>('latest');

  const filteredSeries = useMemo(() => {
    let list = activeSlug === 'all'
      ? [...tvSeriesData]
      : tvSeriesData.filter((s) => s.categorySlug === activeSlug);

    if (sortBy === 'rating') {
      list.sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'popular') {
      list.sort((a, b) => b.views - a.views);
    } else {
      list.sort((a, b) => b.year - a.year);
    }

    return list;
  }, [activeSlug, sortBy]);

  const categoryName = categoryNames[activeSlug] || '全部剧集';

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-20 pb-16">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryName },
              ]}
            />
          </div>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
              {categoryName}
            </h1>
            <p className="text-sm text-foreground-500">
              {activeSlug === 'all'
                ? '浏览所有电视剧，发现你喜欢的精彩内容'
                : categories.find((c) => c.slug === activeSlug)?.description || ''}
            </p>
          </div>

          {/* Category Tabs */}
          <div className="mb-6">
            <CategoryTabs
              categories={categories}
              activeSlug={activeSlug}
              onCategoryChange={(newSlug) => {
                window.location.href = `/category/${newSlug}`;
              }}
            />
          </div>

          {/* Sort Controls */}
          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-foreground-500">
              共 <strong className="text-foreground-800">{filteredSeries.length}</strong> 部剧集
            </p>
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              {[
                { value: 'latest', label: '最新' },
                { value: 'rating', label: '评分' },
                { value: 'popular', label: '热门' },
              ].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setSortBy(opt.value as typeof sortBy)}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    sortBy === opt.value
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-600 hover:text-foreground-800'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Series Grid */}
          {filteredSeries.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
              {filteredSeries.map((series) => (
                <TVCard key={series.id} series={series} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-movie-line text-foreground-400 text-2xl w-8 h-8 flex items-center justify-center"></i>
              </div>
              <p className="text-foreground-500">暂无该分类的剧集</p>
            </div>
          )}

          {/* Pagination */}
          {filteredSeries.length > 10 && (
            <div className="flex items-center justify-center gap-2 mt-10">
              <button className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer">
                <i className="ri-arrow-left-s-line w-4 h-4 flex items-center justify-center"></i>
              </button>
              {[1, 2, 3].map((page) => (
                <button
                  key={page}
                  className={`w-9 h-9 rounded-lg flex items-center justify-center text-sm font-medium transition-colors cursor-pointer ${
                    page === 1
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {page}
                </button>
              ))}
              <button className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer">
                <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
              </button>
            </div>
          )}
        </div>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `窝窝影院 - ${categoryName}`,
            description: `窝窝影院${categoryName}分类，免费在线观看${categoryName}全集，高清不缓冲流畅播放。`,
            url: `https://wowocinema.com/category/${activeSlug}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '窝窝影院',
              url: 'https://wowocinema.com/',
            },
          }),
        }}
      />
    </div>
  );
}